/**
 * P9.2/P9.3 (Juni 2026) – „Tarif & Abrechnung" (READ-ONLY Transparenz + Wechsel-ANFRAGE).
 *
 * Zeigt Tarif, aktive Mitarbeiter-Auslastung, Limit, freie Plätze, Netto-Preise.
 * „Tarif wechseln" öffnet den Tarifvergleich und erstellt – nach ausdrücklicher
 * Bestätigung – NUR eine `plan_change_request` (KEIN Geldfluss, KEIN Stripe,
 * KEIN Checkout).
 * ZAHL-02b (Betreiberfreigabe 13.10.): „Zahlungsdaten ändern" / „Rechnungen ansehen" öffnen das
 * Stripe-Kundenportal über POST /api/billing/portal-session (nur bei vorhandenem Abo-Kunden);
 * Badge „Zahlung offen" / „Freigeschaltet", Hinweis „Zahlung fehlgeschlagen" bei offener Zahlung.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
// ZAHL-02b (Betreiberfreigabe 13.10.): Stripe-Kundenportal + wortgleiche Zahlungs-Texte.
import {
  OFFENE_ZAHLUNG_LESEZUGRIFF_TEXT,
  ZAHLUNG_OFFEN_TEXT,
  openBillingPortal,
  portalErrorText,
  vertragsendeTarifText,
} from "@/lib/accountPolicy";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CreditCard, Users, Info, Clock, X, AlertTriangle, Building2, ShieldCheck, BadgeCheck } from "lucide-react";
import TarifvergleichDialog from "@/components/TarifvergleichDialog";
// MOD-01b-3 (Betreiberfreigabe 13.10.): Abschnitt „Zusatzmodule" (eigene Unterkomponente).
import ZusatzmoduleAbschnitt from "@/components/ZusatzmoduleAbschnitt";
// ABO-KNOPF-03, Betreiberfreigabe 30.10.2026: Sichtbarkeitsregel „Abo abschließen“ aus lib/aboAbschluss.js.
import { aboAbschlussAnzeige } from "@/lib/aboAbschluss";

// BUCH-01 (Betreiberfreigabe 12.10.): Rückmeldung nach Stripe – Texte wortgleich.
const CHECKOUT_RESULT_TEXT = {
  aktiv: "Vielen Dank. Ihr Abo ist aktiv.",
  pruefung:
    "Vielen Dank. Ihre Zahlung wird bestätigt. Bei Lastschrift kann das einige Werktage dauern. " +
    "Sie können CLARTEXT in dieser Zeit uneingeschränkt nutzen.",
  verarbeitung: "Ihre Buchung wird verarbeitet. Bitte laden Sie die Seite in einigen Minuten neu.",
  abbruch: "Der Abschluss wurde abgebrochen. Es wurde nichts berechnet.",
};
const PAYMENT_PENDING_TEXT =
  "Ihre Zahlung wird bestätigt. Bei Lastschrift kann das einige Werktage dauern. " +
  "Sie können CLARTEXT in dieser Zeit uneingeschränkt nutzen.";
const RETURN_RELOADS = 3;
const RETURN_INTERVAL_MS = 3000;

function readCheckoutReturn() {
  if (typeof window === "undefined") return null;
  const sp = new URLSearchParams(window.location.search);
  return sp.get("checkout");
}

function stripCheckoutParams() {
  const url = new URL(window.location.href);
  url.searchParams.delete("checkout");
  url.searchParams.delete("session_id");
  window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
}

function evaluateReturn(summary) {
  const oe = summary?.owner_experience || {};
  const access = oe.access_state || {};
  if (access.license_status === "aktiv_durch_zahlung") return "aktiv";
  if (access.payment_pending) return "pruefung";
  return null;
}

// Block 3 Stufe II (Juli 2026) – Tarifwechsel-Einstieg fail-closed ausgeblendet
// (alte Platzhalterpreise). Muster identisch zu DEMO_UPGRADE_ENABLED.
const TARIFWECHSEL_ENABLED =
  process.env.REACT_APP_TARIFWECHSEL_ENABLED === "true";

function fmtEuro(cents) {
  if (cents == null) return "–";
  return (
    (cents / 100).toLocaleString("de-DE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + " €"
  );
}

const INTERVAL_LABEL = { monthly: "monatlich", yearly: "jährlich" };

// Mehrsprachigkeit (Juni 2026): Anzeige-Namen der buchbaren Mitarbeiter-Sprachen
// (Reihenfolge = Modell-/Katalogreihenfolge, "de" ist immer kostenlos enthalten).
const EMPLOYEE_LANG_NAMES = {
  en: "Englisch",
  pl: "Polnisch",
  ro: "Rumänisch",
  uk: "Ukrainisch",
  bg: "Bulgarisch",
  tr: "Türkisch",
  hr: "Kroatisch",
  ru: "Russisch",
};
const REQ_STATUS_LABEL = {
  pending: "ausstehend",
  approved: "freigegeben (Aktivierung folgt)",
  rejected: "abgelehnt",
  canceled: "storniert",
};

// M5.4 – Warnstufe → Stil (read-only Hinweise).
const WARN_STYLE = {
  info: "border-sky-300 bg-sky-50 text-sky-900",
  warning: "border-amber-300 bg-amber-50 text-amber-900",
  danger: "border-red-300 bg-red-50 text-red-900",
};

export default function TarifAbrechnungCard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pending, setPending] = useState(null);
  const [checkoutBusy, setCheckoutBusy] = useState(false);
  // LIMIT-01 (Betreiberfreigabe 12.10.): 409 angebot_erforderlich → verständlicher Hinweis.
  const [angebotHinweis, setAngebotHinweis] = useState(false);
  // PRICE-03b-2: Preis je aktivem Mitarbeiter aus DERSELBEN Quelle wie /abo
  // (öffentlicher Katalog), NICHT aus den Code-Plänen der Billing-Summary.
  const [perSeatCents, setPerSeatCents] = useState(null);
  // BUCH-01b (Betreiberfreigabe 12.10.): Name des Abo-Katalogprodukts (Badge) – aus dem Katalog.
  const [aboProductLabel, setAboProductLabel] = useState(null);
  // ZAHL-02b (Betreiberfreigabe 13.10., Z2): Stripe-Kundenportal (Zahlungsdaten / Rechnungen).
  const [portalBusy, setPortalBusy] = useState(null);
  const [portalError, setPortalError] = useState("");

  const openPortal = async (ziel) => {
    if (portalBusy) return;
    setPortalBusy(ziel);
    setPortalError("");
    try {
      await openBillingPortal(api, ziel);
    } catch (e) {
      const msg = portalErrorText(e);
      setPortalError(msg);
      toast.error(msg);
    } finally {
      setPortalBusy(null);
    }
  };

  const loadRequests = useCallback(() => {
    api
      .getPlanChangeRequests()
      .then((d) => {
        const open = (d.requests || []).find((r) => r.status === "pending");
        setPending(open || null);
      })
      .catch(() => {});
  }, []);

  const loadSummary = useCallback(() => {
    api.getBillingSummary().then(setData).catch(() => setError(true));
  }, []);

  useEffect(() => {
    loadSummary();
    loadRequests();
  }, [loadSummary, loadRequests]);

  // PRICE-03b-2: Preis je aktivem Mitarbeiter ausschließlich aus dem öffentlichen
  // Katalog (Quelle wie /abo), pricing.monthly_net_cents des per_employee-Produkts.
  useEffect(() => {
    let active = true;
    api
      .getPublicCatalog()
      .then((d) => {
        if (!active) return;
        const products = d && Array.isArray(d.products) ? d.products : [];
        const abo = products.find(
          (p) =>
            p.pricing &&
            (p.pricing.per_employee === true ||
              p.pricing.product_type === "subscription") &&
            p.pricing.monthly_net_cents != null,
        );
        setPerSeatCents(abo ? abo.pricing.monthly_net_cents : null);
        setAboProductLabel(abo && typeof abo.label === "string" && abo.label.trim() ? abo.label.trim() : null);
      })
      .catch(() => {
        if (active) setPerSeatCents(null);
        if (active) setAboProductLabel(null);
      });
    return () => {
      active = false;
    };
  }, []);

  const cancelRequest = async () => {
    if (!pending) return;
    try {
      await api.cancelPlanChangeRequest(pending.id);
      toast.success("Anfrage storniert.");
      loadRequests();
    } catch {
      toast.error("Stornierung fehlgeschlagen.");
    }
  };

  // BUCH-01 (Betreiberfreigabe 12.10.): Rückmeldung nach Stripe – ausschließlich aus
  // GET /api/billing/summary (keine Schreibaktion); Query-Parameter werden entfernt.
  const [checkoutReturn] = useState(readCheckoutReturn);
  const [checkoutResult, setCheckoutResult] = useState(null);
  useEffect(() => {
    if (!checkoutReturn) return undefined;
    stripCheckoutParams();
    if (checkoutReturn === "cancel") {
      setCheckoutResult("abbruch");
      return undefined;
    }
    if (checkoutReturn !== "success") return undefined;
    let cancelled = false;
    let reloads = 0;
    let timer = null;
    const tick = async () => {
      let d = null;
      try {
        d = await api.getBillingSummary();
      } catch {
        d = null;
      }
      if (cancelled) return;
      if (d) setData(d);
      const r = evaluateReturn(d);
      if (r) {
        setCheckoutResult(r);
        return;
      }
      if (reloads >= RETURN_RELOADS) {
        setCheckoutResult("verarbeitung");
        return;
      }
      reloads += 1;
      timer = setTimeout(tick, RETURN_INTERVAL_MS);
    };
    tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [checkoutReturn]);

  // BUCH-01: Anker #tarif (V5) – Ziel aller „Abo abschließen"-Hinweise.
  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash === "#tarif") {
      const el = document.getElementById("tarif");
      if (el && typeof el.scrollIntoView === "function") el.scrollIntoView({ block: "start" });
    }
  }, [data]);

  // R6-0c – App-Checkout: ruft NUR den bestehenden Backend-Endpunkt
  // (POST /api/billing/checkout, Body nur origin_url) und leitet bei Erfolg auf die
  // Stripe-Session-URL weiter. KEINE Preisberechnung, KEINE Stripe-Parameter im Frontend.
  const startCheckout = async () => {
    if (checkoutBusy) return;
    setCheckoutBusy(true);
    try {
      const res = await api.createCheckout(window.location.origin);
      if (res && res.url) {
        window.location.assign(res.url); // Redirect – keine weitere Verarbeitung
        return;
      }
      toast.error("Checkout konnte nicht gestartet werden.");
      setCheckoutBusy(false);
    } catch (e) {
      const status = e && e.response ? e.response.status : null;
      // LIMIT-01: ab 51 Mitarbeitern individuelles Angebot (kein technischer Fehler).
      if (status === 409 && e.response?.data?.detail?.code === "angebot_erforderlich") {
        setAngebotHinweis(true);
        setCheckoutBusy(false);
        return;
      }
      const msg =
        status === 401
          ? "Bitte erneut anmelden."
          : status === 409
          ? "Checkout ist für Ihren Tarif aktuell nicht möglich."
          : "Checkout konnte nicht gestartet werden.";
      toast.error(msg);
      setCheckoutBusy(false);
    }
  };

  if (error) {
    return (
      <Card className="bg-white border border-slate-200 shadow-sm p-6" data-testid="einstellungen-tarif-card">
        <p className="text-sm text-slate-500">Tarif-Informationen konnten nicht geladen werden.</p>
      </Card>
    );
  }
  if (!data) {
    return (
      <Card className="bg-white border border-slate-200 shadow-sm p-6" data-testid="einstellungen-tarif-card">
        <p className="text-sm text-slate-500">Lade Tarif-Informationen…</p>
      </Card>
    );
  }

  const unlimited = data.max_employees == null;
  const oe = data.owner_experience || {};
  const paymentEnabled = !!oe.payment_actions_enabled;
  const usage = data.usage_percent;
  const barColor =
    usage == null
      ? "bg-slate-300"
      : usage >= 100
      ? "bg-red-500"
      : usage >= 80
      ? "bg-orange-500"
      : "bg-emerald-500";
  const limitReached =
    !unlimited && (data.seats_remaining === 0 || (usage != null && usage >= 100));

  // BUCH-01 (Betreiberfreigabe 12.10.): Zustand → Button/Text.
  const access = oe.access_state || {};
  const paymentPending = !!access.payment_pending;
  // ABO-KNOPF-03, Betreiberfreigabe 30.10.2026: hideAboButton nur noch über aboAbschlussAnzeige (eine Stelle).
  const hideAboButton = !aboAbschlussAnzeige(
    { access_state: oe.access_state, account_status: data.account_status, trial_ends_at: data.trial_ends_at },
    Date.now(),
  ).knopf;

  // BUCH-01b (Betreiberfreigabe 12.10.): Badge ohne Namen des alten Staffelmodells.
  // Pilot/Enterprise unverändert; Abo-Kunde (bezahlte Lizenz, offene Zahlung oder
  // Zahlung in Prüfung) → Katalogname, sonst „Abo"; ohne bezahlte Lizenz → „Testphase".
  // ZAHL-02b (Betreiberfreigabe 13.10., Soll 6): Zustand „Zahlung offen" → „Zahlung offen";
  // E1 (Zugang ohne Zahlungsbezug, access_state.aktiv_ohne_zahlung) → „Freigeschaltet".
  const isPilotOrEnterprise =
    !!oe.is_pilot || !!oe.is_enterprise ||
    oe.online_payment_state === "pilot" || oe.online_payment_state === "enterprise";
  const paymentState = oe.payment_state || {};
  const zahlungOffen = !!paymentState.zahlung_offen;
  const zahlungskunde = !!paymentState.zahlungskunde;
  // ZAHL-02c (Betreiberfreigabe 13.10., Soll 5): Vertragslaufzeit aus dem Summary (null außerhalb Geltungsbereich).
  const vertrag = oe.vertrag || null;
  // ZAHL-02d (Betreiberfreigabe 14.10., Soll 5): Vertragsende (Lesephase) aus access_state bzw. Vertragsprojektion.
  const vertragsende = access.vertragsende || vertrag?.vertragsende || null;
  const vertragBeendet = access.license_status === "vertrag_beendet" || !!vertragsende;
  const hasAbo =
    access.license_status === "aktiv_durch_zahlung" || access.license_status === "inaktiv" || paymentPending;
  const badgeLabel = isPilotOrEnterprise
    ? data.current_plan_name
    : vertragBeendet
    ? "Vertrag beendet"
    : zahlungOffen
    ? "Zahlung offen"
    : hasAbo
    ? aboProductLabel || "Abo"
    : access.aktiv_ohne_zahlung === true
    ? "Freigeschaltet"
    : "Testphase";

  // Mehrsprachigkeit (Juni 2026): Banner-Zustand aus den gebuchten Sprachen.
  const langEnt = data.language_entitlement || null;
  const bookedLangs = langEnt ? langEnt.entitled_languages || [] : [];
  const missingLangs = Object.keys(EMPLOYEE_LANG_NAMES).filter((c) => !bookedLangs.includes(c));
  const langVariant =
    bookedLangs.length === 0 ? "none_booked" : missingLangs.length === 0 ? "all_booked" : "partial";
  const bookedNames = bookedLangs.map((c) => EMPLOYEE_LANG_NAMES[c] || c).join(", ");
  const missingNames = missingLangs.map((c) => EMPLOYEE_LANG_NAMES[c]).join(", ");

  return (
    <Card
      id="tarif"
      className="bg-white border border-slate-200 shadow-sm p-6 space-y-5"
      data-testid="einstellungen-tarif-card"
    >
      {/* BUCH-01: Rückmeldung nach Rückkehr von Stripe (nur Anzeige). */}
      {checkoutResult && (
        <div
          role="status"
          className={
            checkoutResult === "abbruch"
              ? "rounded-lg border border-slate-300 bg-slate-50 p-4 text-sm text-slate-800"
              : "rounded-lg border border-emerald-300 bg-emerald-50 p-4 text-sm text-emerald-900"
          }
          data-testid="tarif-checkout-result"
          data-result={checkoutResult}
        >
          {CHECKOUT_RESULT_TEXT[checkoutResult]}
        </div>
      )}
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-md bg-indigo-600 text-white flex items-center justify-center shrink-0">
          <CreditCard className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <h2 className="font-heading text-lg font-semibold text-slate-900">Tarif &amp; Abrechnung</h2>
          <p className="text-sm text-slate-500">
            Ihr aktueller Tarif und die Mitarbeiter-Auslastung. Abgerechnet wird
            ausschließlich nach <strong>aktiven Mitarbeitern</strong>.
          </p>
        </div>
        <Badge
          variant="outline"
          data-testid="tarif-current-plan"
          className="bg-indigo-50 border-indigo-200 text-indigo-800 font-medium"
        >
          {badgeLabel}
          {data.billing_interval ? ` · ${INTERVAL_LABEL[data.billing_interval] || data.billing_interval}` : ""}
        </Badge>
      </div>

      {/* M5.4 – Vertrags- & Zahlungsstatus (READ-ONLY, keine internen IDs/Secrets) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="tarif-status-grid">
        <div className="rounded-lg border border-slate-200 p-4">
          <div className="text-xs uppercase tracking-wide text-slate-400">Vertragsstatus</div>
          <div
            className="font-semibold text-slate-900"
            data-testid="tarif-contract-status"
            data-domain-status={oe.domain_status || ""}
          >
            {oe.status_label || "–"}
          </div>
        </div>
        <div className="rounded-lg border border-slate-200 p-4">
          <div className="text-xs uppercase tracking-wide text-slate-400">Zahlungsstatus</div>
          <div className="font-semibold text-slate-900" data-testid="tarif-payment-status">
            {oe.payment_status_label || "–"}
          </div>
        </div>
      </div>

      {/* Trial – FIX-01b-2: nur, wenn der zentrale Zugriffszustand keinen Vollzugriff
          durch Zahlung/Lizenz ergibt und kein Lesezugriff vorliegt. */}
      {oe.is_trial && !oe.access_state?.read_only && !oe.access_state?.license_status && (
        <div
          className="rounded-lg border border-sky-300 bg-sky-50 p-4 text-sm text-sky-900 flex items-start gap-2"
          data-testid="tarif-trial-hint"
        >
          <Clock className="w-4 h-4 mt-0.5 shrink-0 text-sky-600" />
          <span>
            Sie befinden sich in der <strong>Testphase</strong>
            {oe.trial_days_left != null ? ` – noch ${oe.trial_days_left} Tag(e)` : ""}. Es erfolgt
            keine automatische Zahlung.
          </span>
        </div>
      )}

      {/* ABO-KNOPF-02, Betreiberfreigabe 29.10.2026: Hauptknopf (Standardstil statt outline) direkt unter
          Status/Testphasen-Hinweis, vor den Modulkarten; Handler, Bedingungen, testid und Text unverändert.
          BUCH-01 (Betreiberfreigabe 12.10.): „Abo abschließen" statt „Zahlungsart
          hinterlegen" – Funktion unverändert (bestehender Abo-Checkout inkl. LIMIT-01). */}
      {!hideAboButton && (
        <div>
          <Button
            disabled={!paymentEnabled || checkoutBusy}
            onClick={paymentEnabled ? startCheckout : undefined}
            data-testid="tarif-payment-button"
          >
            Abo abschließen
          </Button>
        </div>
      )}

      {/* FIX-01b-2 (Betreiberfreigabe 12.10.): Lesezugriff-Hinweis aus access_state. */}
      {oe.access_state?.read_only && oe.access_state.reason === "read_only_expired" && (
        <div
          className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-900"
          data-testid="tarif-readonly-hint"
          data-reason="read_only_expired"
        >
          <strong>Testphase abgelaufen:</strong> Ihr Konto befindet sich im Lesezugriff. Bestehende
          Inhalte und Nachweise bleiben verfügbar.
        </div>
      )}
      {oe.access_state?.read_only && oe.access_state.reason === "read_only_license_inaktiv" && (
        <div
          className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
          data-testid="tarif-readonly-hint"
          data-reason="read_only_license_inaktiv"
        >
          {/* ZAHL-02b (Betreiberfreigabe 13.10., Soll 6): Wortlaut wie Banner „Offene Zahlung". */}
          <strong>Offene Zahlung:</strong> {OFFENE_ZAHLUNG_LESEZUGRIFF_TEXT}
        </div>
      )}
      {/* ZAHL-02d (Betreiberfreigabe 14.10., Soll 5): Lesephase nach Vertragsende – Text wortgleich. */}
      {oe.access_state?.read_only && oe.access_state.reason === "read_only_vertragsende" && (
        <div
          className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-900"
          data-testid="tarif-readonly-hint"
          data-reason="read_only_vertragsende"
        >
          {vertragsendeTarifText(vertragsende)}
        </div>
      )}
      {/* ZAHL-02b (Betreiberfreigabe 13.10., Soll 6): „Zahlung offen" mit vollem Zugriff (past_due). */}
      {zahlungOffen && !oe.access_state?.read_only && (
        <div
          className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
          data-testid="tarif-zahlung-offen-hint"
        >
          <strong>Zahlung fehlgeschlagen:</strong> {ZAHLUNG_OFFEN_TEXT}
        </div>
      )}

      {/* Online-Zahlungs-Zustand (read-only Erklärung, KEIN Stripe-Call) */}
      {oe.online_payment_state === "not_enabled" && (
        <div
          className="rounded-lg border border-slate-300 bg-slate-50 p-4 text-sm text-slate-700 flex items-start gap-2"
          data-testid="tarif-checkout-disabled"
        >
          <Info className="w-4 h-4 mt-0.5 shrink-0 text-slate-500" />
          <span>
            <strong>Online-Zahlung ist noch nicht aktiviert.</strong> Es werden keine echten
            Zahlungen ausgelöst. Ein Tarifwechsel ist bereits als unverbindliche Anfrage möglich.
          </span>
        </div>
      )}
      {oe.online_payment_state === "enterprise" && (
        <div
          className="rounded-lg border border-violet-300 bg-violet-50 p-4 text-sm text-violet-900 flex items-start gap-2"
          data-testid="tarif-enterprise-hint"
        >
          <Building2 className="w-4 h-4 mt-0.5 shrink-0 text-violet-600" />
          <span>
            <strong>Enterprise-Tarif:</strong> individueller Vertrag, kein Online-Checkout. Für ein
            Angebot oder Anpassungen kontaktieren Sie bitte den Betreiber.
          </span>
        </div>
      )}
      {oe.online_payment_state === "pilot" && (
        <div
          className="rounded-lg border border-slate-300 bg-slate-50 p-4 text-sm text-slate-700 flex items-start gap-2"
          data-testid="tarif-pilot-hint"
        >
          <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0 text-slate-500" />
          <span>Interner Referenztarif – keine Online-Zahlung erforderlich.</span>
        </div>
      )}
      {oe.online_payment_state === "available" && (
        <div
          className="rounded-lg border border-emerald-300 bg-emerald-50 p-4 text-sm text-emerald-900 flex items-start gap-2"
          data-testid="tarif-checkout-available"
        >
          <BadgeCheck className="w-4 h-4 mt-0.5 shrink-0 text-emerald-600" />
          <span>Online-Zahlung ist verfügbar. Sie können einen Tarif auswählen und bezahlen.</span>
        </div>
      )}

      {/* Echte Warnungen (Trial-Ablauf, Zahlungsstörung) */}
      {oe.warnings && oe.warnings.length > 0 && (
        <div className="space-y-2" data-testid="tarif-oe-warnings">
          {oe.warnings.map((w, i) => (
            <div
              key={i}
              className={`rounded-lg border p-3 text-sm flex items-start gap-2 ${WARN_STYLE[w.level] || WARN_STYLE.info}`}
              data-testid={`tarif-oe-warning-${w.level}`}
            >
              {w.level === "info" ? (
                <Info className="w-4 h-4 mt-0.5 shrink-0" />
              ) : (
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              )}
              <span>{w.text}</span>
            </div>
          ))}
        </div>
      )}

      {/* Offene Wechsel-Anfrage */}
      {pending && (
        <div
          className="rounded-lg border border-sky-300 bg-sky-50 p-4 text-sm text-sky-900 flex items-start gap-2"
          data-testid="tarif-pending-request"
        >
          <Clock className="w-4 h-4 mt-0.5 shrink-0 text-sky-600" />
          <div className="flex-1">
            Wechsel-Anfrage zu <strong>{pending.requested_plan_name || pending.requested_plan}</strong>
            {pending.requested_interval ? ` (${INTERVAL_LABEL[pending.requested_interval]})` : ""} – Status:{" "}
            <strong>{REQ_STATUS_LABEL[pending.status] || pending.status}</strong>. Es erfolgt noch keine Zahlung.
          </div>
          <Button variant="ghost" size="sm" onClick={cancelRequest} data-testid="tarif-cancel-request">
            <X className="w-4 h-4 mr-1" />
            Stornieren
          </Button>
        </div>
      )}

      {/* Auslastung */}
      <div className="rounded-lg border border-slate-200 p-4 space-y-3">
        <div className="flex items-center justify-between text-sm">
          <span className="flex items-center gap-2 text-slate-600">
            <Users className="w-4 h-4 text-slate-400" />
            Aktive Mitarbeiter
          </span>
          <span className="font-semibold text-slate-900" data-testid="tarif-usage-count">
            {data.active_employee_count}
            {unlimited ? " (unbegrenzt)" : ` / ${data.max_employees}`}
          </span>
        </div>
        {!unlimited && (
          <>
            <div className="h-2.5 w-full rounded-full bg-slate-100 overflow-hidden">
              <div
                className={`h-full ${barColor} transition-all`}
                style={{ width: `${Math.min(usage ?? 0, 100)}%` }}
                data-testid="tarif-usage-bar"
              />
            </div>
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span data-testid="tarif-seats-remaining">
                {data.seats_remaining === 1 ? "1 Platz frei" : `${data.seats_remaining} Plätze frei`}
              </span>
              <span>{usage}% ausgelastet</span>
            </div>
          </>
        )}
      </div>

      {/* Mehrsprachigkeit (Juni 2026): Hinweisbanner nach dem Muster des
          Fahrer-Add-on-Banners (UnternehmerFuehrerschein). KEIN Checkout —
          Preis lt. Katalog bewusst offen (O-5/O-9); nur Kontakt-Hinweis. */}
      {langEnt && (
        <div
          data-testid="tarif-multilanguage-banner"
          data-variant={langVariant}
          className={`rounded-md border px-4 py-3 text-sm ${
            langVariant === "all_booked"
              ? "border-emerald-200 bg-emerald-50"
              : "border-amber-200 bg-amber-50"
          }`}
        >
          <div className="flex items-start gap-3">
            {langVariant === "all_booked" ? (
              <BadgeCheck className="h-5 w-5 text-emerald-700 shrink-0 mt-0.5" />
            ) : (
              <AlertTriangle className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
            )}
            <div
              className={`flex-1 ${
                langVariant === "all_booked" ? "text-emerald-900" : "text-amber-900"
              }`}
            >
              {langVariant === "none_booked" ? (
                <>
                  <span className="font-medium">
                    Mehrsprachigkeit für Mitarbeiter noch nicht gebucht:
                  </span>{" "}
                  Ihre Mitarbeiter können die Bedienoberfläche und Unterweisungen in bis zu
                  8 weiteren Sprachen nutzen (Englisch, Polnisch, Rumänisch, Ukrainisch,
                  Bulgarisch, Türkisch, Kroatisch, Russisch).{" "}
                  {/* MOD-01b-3 (Betreiberfreigabe 13.10., Soll 7): Verweis auf den Abschnitt
                      „Zusatzmodule" statt auf den Support. */}
                  <span data-testid="tarif-multilanguage-cta">
                    Weitere Sprachen schalten Sie mit dem{" "}
                    <a
                      href="#zusatzmodule"
                      className="underline font-medium"
                      data-testid="tarif-multilanguage-cta-link"
                      onClick={() => {
                        const el = document.getElementById("zusatzmodule");
                        if (el && typeof el.scrollIntoView === "function") el.scrollIntoView({ block: "start" });
                      }}
                    >
                      Zusatzmodul „Unterweisung in Muttersprache"
                    </a>{" "}
                    frei.
                  </span>
                </>
              ) : langVariant === "all_booked" ? (
                <>
                  <span className="font-medium">Mitarbeiter-Sprachen gebucht:</span>{" "}
                  <span data-testid="tarif-multilanguage-booked">{bookedNames}</span>. Alle
                  verfügbaren Sprachen sind für Ihre Mitarbeiter freigeschaltet.
                </>
              ) : (
                <>
                  <span className="font-medium">Gebuchte Mitarbeiter-Sprachen:</span>{" "}
                  <span data-testid="tarif-multilanguage-booked">{bookedNames}</span>.{" "}
                  Weitere Sprachen für Ihre Mitarbeiter verfügbar ({missingNames}) — wenden
                  Sie sich an den CLartext-Support, um weitere Sprachen freizuschalten.
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {limitReached && (
        <div
          className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 flex items-start gap-2"
          data-testid="tarif-limit-hint"
        >
          <Info className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
          <span>
            Ihr aktueller Tarif erlaubt maximal <strong>{data.max_employees}</strong> aktive Mitarbeiter.
            {" "}Für weitere Mitarbeiter kontaktieren Sie bitte den Betreiber.
          </span>
        </div>
      )}

      {/* PRICE-03b-2: Preis je aktivem Mitarbeiter aus dem öffentlichen Katalog
          (Quelle wie /abo). KEINE Plan-Preise (49/94/139/199 €) mehr. */}
      {perSeatCents != null ? (
        <div className="rounded-lg border border-slate-200 p-4" data-testid="tarif-price-per-seat">
          <div className="text-xs uppercase tracking-wide text-slate-400">
            Preis je aktivem Mitarbeiter
          </div>
          <div
            className="font-heading text-2xl font-bold text-slate-900"
            data-testid="tarif-price-monthly"
          >
            {fmtEuro(perSeatCents)} je aktivem Mitarbeiter im Monat
          </div>
          <div className="text-xs text-slate-500">netto zzgl. MwSt.</div>
          {data.active_employee_count != null && (
            <div className="text-sm text-slate-700 mt-2" data-testid="tarif-price-current-total">
              Aktuell {data.active_employee_count} aktive Mitarbeiter ={" "}
              {fmtEuro(perSeatCents * data.active_employee_count)} im Monat, netto zzgl. MwSt.
            </div>
          )}
        </div>
      ) : (
        <div
          className="rounded-lg border border-slate-200 p-4 text-sm text-slate-500"
          data-testid="tarif-price-unavailable"
        >
          Preis derzeit nicht verfügbar.
        </div>
      )}

      {/* BUCH-01: „Nächsthöherer Tarif" (altes Staffelmodell) entfernt. */}

      {/* MOD-01b-3 (Betreiberfreigabe 13.10.): Zusatzmodule – unterhalb der Abo-Angaben,
          nur Owner (wie die übrige Tarifkarte). Anker #zusatzmodule. */}
      <ZusatzmoduleAbschnitt />

      {/* Aktionen */}
      <div className="border-t border-slate-100 pt-4 space-y-3">
        <div className="flex flex-wrap gap-2">
          {TARIFWECHSEL_ENABLED && (
            <Button onClick={() => setDialogOpen(true)} data-testid="tarif-change-button">
              Tarif wechseln
            </Button>
          )}
          {/* ABO-KNOPF-02, Betreiberfreigabe 29.10.2026: „Abo abschließen" steht jetzt oben unter Status/Testphasen-Hinweis. */}
          {/* BUCH-01b (Betreiberfreigabe 12.10.): „Rechnungen anzeigen" entfällt, solange es
              keinen funktionierenden Rechnungsweg gibt (vorher hart deaktiviert). */}
          {/* ZAHL-02b (Betreiberfreigabe 13.10., Z2): Stripe-Kundenportal – nur bei vorhandenem
              Abo-Kunden (stripe_customer_id); öffnet in derselben Registerkarte. */}
          {zahlungskunde && (
            <>
              <Button
                variant="outline"
                disabled={!!portalBusy}
                onClick={() => openPortal("zahlungsdaten")}
                data-testid="tarif-portal-zahlungsdaten"
              >
                Zahlungsdaten ändern
              </Button>
              <Button
                variant="outline"
                disabled={!!portalBusy}
                onClick={() => openPortal("rechnungen")}
                data-testid="tarif-portal-rechnungen"
              >
                Rechnungen ansehen
              </Button>
            </>
          )}
        </div>
        {portalError && (
          <p
            className="text-sm text-red-900 bg-red-50 border border-red-200 rounded-md px-3 py-2"
            data-testid="tarif-portal-error"
            role="alert"
          >
            {portalError}
          </p>
        )}
        {!hideAboButton && perSeatCents != null && (
          <p className="text-xs text-slate-600" data-testid="tarif-abo-preiszeile">
            {fmtEuro(perSeatCents)} netto je aktivem Mitarbeiter und Monat · monatliche Abrechnung · 12
            Monate Mindestlaufzeit
          </p>
        )}
        {/* ZAHL-02c (Betreiberfreigabe 13.10., Soll 5): Vertragslaufzeit (nur abo_pro_mitarbeiter mit
            bekanntem Vertragsbeginn, Daten aus dem Backend). Kein Kündigungsbutton (K4).
            ZAHL-02d: nach Vertragsende entfällt die Laufzeitangabe (Hinweis „Vertrag beendet am …" oben). */}
        {vertrag?.vertragsbeginn_text && !vertragBeendet && (
          <p
            className="text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-md px-3 py-2"
            data-testid="tarif-vertragslaufzeit"
            data-gekuendigt={vertrag.gekuendigt ? "1" : "0"}
          >
            {vertrag.gekuendigt
              ? `Gekündigt zum ${vertrag.gekuendigt_zum_text}. Bis dahin steht Ihnen CLARTEXT vollständig zur Verfügung.`
              : `Vertragslaufzeit bis ${vertrag.laufzeit_bis_text}. Verlängert sich automatisch um 12 Monate, wenn nicht bis ${vertrag.letzter_kuendigungstag_text} gekündigt wird. Kündigungen bitte per E-Mail an info@clartext.io.`}
          </p>
        )}
        {paymentPending && (
          <p
            className="text-sm text-emerald-900 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2"
            data-testid="tarif-payment-pending"
          >
            {PAYMENT_PENDING_TEXT}
          </p>
        )}
        {angebotHinweis && (
          <p
            className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-3 py-2"
            data-testid="tarif-angebot-hinweis"
          >
            Für Betriebe ab 51 Mitarbeitern erstellen wir Ihnen gern ein individuelles Angebot.
            Bitte schreiben Sie uns an{" "}
            <a
              href="mailto:info@clartext.io"
              className="underline font-medium"
              data-testid="tarif-angebot-mailto"
            >
              info@clartext.io
            </a>
            .
          </p>
        )}
        {oe.next_steps && oe.next_steps.length > 0 && (
          <ul
            className="text-xs text-slate-500 space-y-1 list-disc pl-4"
            data-testid="tarif-next-steps"
          >
            {oe.next_steps.map((s, i) => (
              <li key={i} data-testid={`tarif-next-step-${i}`}>
                {s}
              </li>
            ))}
          </ul>
        )}
        {TARIFWECHSEL_ENABLED && (
          <p className="text-xs text-slate-500" data-testid="tarif-soon-hint">
            „Tarif wechseln" erstellt nur eine unverbindliche Anfrage – der Wechsel wird erst nach
            Freigabe/Zahlungsaktivierung wirksam. {data.vat_note}
          </p>
        )}
      </div>

      <TarifvergleichDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onRequested={() => {
          loadRequests();
          loadSummary();
        }}
      />
    </Card>
  );
}
